// src/components/admin/BanUserModal.tsx
// ============================================================
// SALT — Ban/Suspend User Modal
//
// Used from UserDetailPage. Shows a form with:
//   - Ban type toggle (ban vs suspend)
//   - Duration selector (preset or custom hours)
//   - Required reason textarea
//   - Warning about captain reassignment
// ============================================================

import React, { useState } from 'react';

interface BanUserModalProps {
  isOpen: boolean;
  username: string;
  onClose: () => void;
  onSubmit: (data: { type: 'ban' | 'suspend'; reason: string; duration?: number }) => void;
  isSubmitting: boolean;
}

const DURATION_PRESETS = [
  { label: '1 hour', hours: 1 },
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 168 },
  { label: '30 days', hours: 720 },
  { label: '90 days', hours: 2160 },
  { label: 'Permanent', hours: 0 },
];

export default function BanUserModal({
  isOpen,
  username,
  onClose,
  onSubmit,
  isSubmitting,
}: BanUserModalProps) {
  const [type, setType] = useState<'ban' | 'suspend'>('suspend');
  const [durationPreset, setDurationPreset] = useState<number>(24);
  const [customHours, setCustomHours] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    const hours = useCustom ? parseInt(customHours, 10) : durationPreset;
    const duration = hours === 0 ? undefined : hours;

    onSubmit({ type, reason: reason.trim(), duration });
  };

  const isValid =
    reason.trim().length >= 3 &&
    (!useCustom || (parseInt(customHours, 10) > 0 && parseInt(customHours, 10) <= 87600));

  return (
    <div className="sl-modal-backdrop" onClick={onClose}>
      <div
        className="sl-modal sl-ban-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${type === 'ban' ? 'Ban' : 'Suspend'} ${username}`}
      >
        <h3 className="sl-modal-title">
          {type === 'ban' ? '🚫 Ban' : '⏸️ Suspend'} {username}
        </h3>

        {/* Type Toggle */}
        <div className="sl-ban-type-toggle">
          <button
            type="button"
            className={`sl-ban-type-btn ${type === 'suspend' ? 'sl-ban-type-btn--active' : ''}`}
            onClick={() => setType('suspend')}
          >
            Suspend
          </button>
          <button
            type="button"
            className={`sl-ban-type-btn sl-ban-type-btn--danger ${type === 'ban' ? 'sl-ban-type-btn--active' : ''}`}
            onClick={() => setType('ban')}
          >
            Ban
          </button>
        </div>

        {type === 'ban' && (
          <div className="sl-ban-warning">
            Banning removes the user from all teams. If they are a captain, captaincy will be reassigned to the longest-serving member.
          </div>
        )}

        {type === 'suspend' && (
          <div className="sl-ban-info">
            Suspending prevents login but preserves team membership. The user will retain their captain status.
          </div>
        )}

        {/* Duration */}
        <div className="sl-ban-section">
          <label className="sl-ban-label">Duration</label>
          {!useCustom ? (
            <div className="sl-ban-duration-grid">
              {DURATION_PRESETS.map((preset) => (
                <button
                  key={preset.hours}
                  type="button"
                  className={`sl-ban-duration-btn ${
                    durationPreset === preset.hours ? 'sl-ban-duration-btn--active' : ''
                  } ${preset.hours === 0 ? 'sl-ban-duration-btn--permanent' : ''}`}
                  onClick={() => setDurationPreset(preset.hours)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="sl-ban-custom-duration">
              <input
                type="number"
                className="sl-ban-input"
                placeholder="Hours"
                value={customHours}
                onChange={(e) => setCustomHours(e.target.value)}
                min={1}
                max={87600}
              />
              <span className="sl-ban-custom-unit">hours</span>
            </div>
          )}
          <button
            type="button"
            className="sl-ban-toggle-custom"
            onClick={() => setUseCustom(!useCustom)}
          >
            {useCustom ? 'Use presets' : 'Custom duration'}
          </button>
        </div>

        {/* Reason */}
        <div className="sl-ban-section">
          <label className="sl-ban-label">
            Reason <span className="sl-required">*</span>
          </label>
          <textarea
            className="sl-ban-textarea"
            placeholder="Provide a reason for this action (minimum 3 characters)..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={1000}
          />
          <div className="sl-ban-char-count">{reason.length}/1000</div>
        </div>

        {/* Actions */}
        <div className="sl-modal-actions">
          <button
            type="button"
            className="sl-modal-button sl-modal-button-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`sl-modal-button ${type === 'ban' ? 'sl-modal-button-danger' : 'sl-modal-button-warning'}`}
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting
              ? 'Processing...'
              : type === 'ban'
              ? 'Ban User'
              : 'Suspend User'}
          </button>
        </div>
      </div>
    </div>
  );
}
